import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "sonner";

import PublicLayout from "./components/layout/PublicLayout";
import Home from "./pages/public/Home";
import Sobre from "./pages/public/Sobre";
import Planos from "./pages/public/Planos";
import BancoDeHoras from "./pages/public/BancoDeHoras";
import ComprarPlano from "./pages/public/ComprarPlano";
import Espacos from "./pages/public/Espacos";
import EspacoDetalhe from "./pages/public/EspacoDetalhe";
import ReservarCotacao from "./pages/public/ReservarCotacao";
import ReservarIdentificacao from "./pages/public/ReservarIdentificacao";
import ReservarPagamento from "./pages/public/ReservarPagamento";
import PagamentoRetorno from "./pages/public/PagamentoRetorno";
import ReservaConfirmada from "./pages/public/ReservaConfirmada";
import Contato from "./pages/public/Contato";
import FAQ from "./pages/public/FAQ";

import Login from "./pages/Login";
import Register from "./pages/Register";

import AdminLayout from "./components/layout/AdminLayout";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminLeads from "./pages/admin/Leads";
import AdminClientes from "./pages/admin/Clientes";
import AdminClienteFicha from "./pages/admin/ClienteFicha";
import AdminEspacos from "./pages/admin/Espacos";
import AdminReservas from "./pages/admin/Reservas";
import AdminPlanos from "./pages/admin/Planos";
import AdminFinanceiro from "./pages/admin/Financeiro";
import AdminPrecificacao from "./pages/admin/Precificacao";
import AdminContratos from "./pages/admin/Contratos";
import AdminRelatorios from "./pages/admin/Relatorios";
import AdminAuditoria from "./pages/admin/Auditoria";
import AdminLeadsSLA from "./pages/admin/LeadsSLA";
import AdminUnidades from "./pages/admin/Unidades";
import AdminConfiguracoes from "./pages/admin/Configuracoes";
import AdminBilling from "./pages/admin/Billing";
import AdminSaaSAdmin from "./pages/admin/SaaSAdmin";
import AdminMetrics from "./pages/admin/Metrics";
import AdminSystemHealth from "./pages/admin/SystemHealth";
import AdminUsuarios from "./pages/admin/Usuarios";
import MeuPerfil from "./pages/MeuPerfil";
import EsqueciSenha from "./pages/EsqueciSenha";
import ResetSenha from "./pages/ResetSenha";
import SaaSLanding from "./pages/public/SaaS";
import Signup from "./pages/Signup";
import Onboarding from "./pages/Onboarding";

import ClientLayout from "./components/layout/ClientLayout";
import ClientDashboard from "./pages/client/Dashboard";
import ClientReservas from "./pages/client/Reservas";
import ClientFaturas from "./pages/client/Faturas";
import ClientContratos from "./pages/client/Contratos";
import ClientPerfil from "./pages/client/Perfil";
import ComprarPacote from "./pages/client/ComprarPacote";
import ExtratoBancoHoras from "./pages/client/ExtratoBancoHoras";

const ADMIN_ROLES = ["super_admin", "admin", "manager", "operator", "reception", "commercial", "financial"];
const USER_MGMT_ROLES = ["super_admin", "admin"];
const AUTHENTICATED_ROLES = ["super_admin", "admin", "manager", "operator", "reception", "commercial", "financial", "client"];

function App() {
  // Capture UTM on first arrival (sessionStorage)
  if (typeof window !== "undefined") {
    try {
      const { captureUTM } = require("./lib/utm");
      captureUTM();
    } catch { /* noop */ }
  }
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors offset={16} mobileOffset={{ top: 72 }} />
          <Routes>
            {/* Public site */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/sobre" element={<Sobre />} />
              <Route path="/planos" element={<Planos />} />
              <Route path="/banco-de-horas" element={<BancoDeHoras />} />
              <Route path="/comprar-plano/:planId" element={<ComprarPlano />} />
              <Route path="/espacos" element={<Espacos />} />
              <Route path="/espacos/:id" element={<EspacoDetalhe />} />
              <Route path="/reservar/cotacao" element={<ReservarCotacao />} />
              <Route path="/reservar/identificacao" element={<ReservarIdentificacao />} />
              <Route path="/reservar/pagamento" element={<ReservarPagamento />} />
              <Route path="/pagamento/retorno" element={<PagamentoRetorno />} />
              <Route path="/reserva/:id/confirmada" element={<ReservaConfirmada />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/faq" element={<FAQ />} />
            </Route>

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/saas" element={<SaaSLanding />} />
            <Route path="/esqueci-senha" element={<EsqueciSenha />} />
            <Route path="/reset-password" element={<ResetSenha />} />
            <Route path="/meu-perfil" element={<ProtectedRoute roles={AUTHENTICATED_ROLES}><MeuPerfil /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute roles={ADMIN_ROLES}><Onboarding /></ProtectedRoute>} />

            {/* Admin */}
            <Route element={<ProtectedRoute roles={ADMIN_ROLES}><AdminLayout /></ProtectedRoute>}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/metrics" element={<AdminMetrics />} />
              <Route path="/admin/leads" element={<AdminLeads />} />
              <Route path="/admin/clientes" element={<AdminClientes />} />
              <Route path="/admin/clientes/:id" element={<AdminClienteFicha />} />
              <Route path="/admin/espacos" element={<AdminEspacos />} />
              <Route path="/admin/reservas" element={<AdminReservas />} />
              <Route path="/admin/planos" element={<AdminPlanos />} />
              <Route path="/admin/financeiro" element={<AdminFinanceiro />} />
              <Route path="/admin/precificacao" element={<AdminPrecificacao />} />
              <Route path="/admin/contratos" element={<AdminContratos />} />
              <Route path="/admin/relatorios" element={<AdminRelatorios />} />
              <Route path="/admin/leads-sla" element={<AdminLeadsSLA />} />
              <Route path="/admin/unidades" element={<AdminUnidades />} />
              <Route path="/admin/configuracoes" element={<AdminConfiguracoes />} />
              <Route path="/admin/billing" element={<AdminBilling />} />
              <Route path="/admin/saas-admin" element={<AdminSaaSAdmin />} />
              <Route path="/admin/auditoria" element={<AdminAuditoria />} />
              <Route path="/admin/system-health" element={<AdminSystemHealth />} />
              <Route path="/admin/usuarios" element={<ProtectedRoute roles={USER_MGMT_ROLES}><AdminUsuarios /></ProtectedRoute>} />
            </Route>

            {/* Client Portal */}
            <Route element={<ProtectedRoute roles={["client"]}><ClientLayout /></ProtectedRoute>}>
              <Route path="/portal" element={<ClientDashboard />} />
              <Route path="/portal/reservas" element={<ClientReservas />} />
              <Route path="/portal/faturas" element={<ClientFaturas />} />
              <Route path="/portal/contratos" element={<ClientContratos />} />
              <Route path="/portal/perfil" element={<ClientPerfil />} />
              <Route path="/portal/comprar-pacote" element={<ComprarPacote />} />
              <Route path="/portal/banco-de-horas" element={<ExtratoBancoHoras />} />
            </Route>

            {/* Redirects amigáveis — login unificado em /login */}
            <Route path="/admin/login" element={<Navigate to="/login" replace />} />
            <Route path="/portal/login" element={<Navigate to="/login" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
